/**
 * Lawn Care Glossary
 *
 * Centralized definitions for lawn care terminology.
 * Used by TermTooltip to provide contextual help for first-time users.
 *
 * Categories:
 * - fertilizer: NPK, application rates, burn prevention
 * - herbicide: pre/post-emergent, weed types
 * - mowing: height, frequency, techniques
 * - watering: schedules, amounts, efficiency
 * - aeration: soil health, compaction, timing
 * - seeding: germination, overseeding, timing
 * - general: grass types, seasons, soil basics
 */

export interface TermDefinition {
  term: string
  shortDef: string
  longDef?: string
  category: 'fertilizer' | 'herbicide' | 'mowing' | 'watering' | 'aeration' | 'seeding' | 'general'
}

export const lawnCareGlossary: Record<string, TermDefinition> = {
  // ============ FERTILIZER TERMS ============
  'NPK': {
    term: 'NPK',
    shortDef: 'Nitrogen-Phosphorus-Potassium ratio',
    longDef: 'The three primary nutrients in fertilizer. Nitrogen (N) promotes leaf growth and green color. Phosphorus (P) supports root development. Potassium (K) improves overall health and stress resistance. A bag labeled "24-0-10" contains 24% nitrogen, 0% phosphorus, and 10% potassium.',
    category: 'fertilizer'
  },
  'nitrogen': {
    term: 'Nitrogen (N)',
    shortDef: 'Primary nutrient for leaf growth and green color',
    longDef: 'The first number in NPK ratios. Nitrogen promotes rapid leaf and blade growth, giving grass its deep green color. Too much can cause excessive growth and disease susceptibility; too little results in pale, slow-growing grass.',
    category: 'fertilizer'
  },
  'phosphorus': {
    term: 'Phosphorus (P)',
    shortDef: 'Nutrient for root development',
    longDef: 'The second number in NPK ratios. Phosphorus supports strong root systems and helps grass establish. Many mature lawns need little additional phosphorus. Some states restrict phosphorus in lawn fertilizers due to water quality concerns.',
    category: 'fertilizer'
  },
  'potassium': {
    term: 'Potassium (K)',
    shortDef: 'Nutrient for stress resistance and overall health',
    longDef: 'The third number in NPK ratios. Potassium strengthens cell walls, improves drought tolerance, and helps grass resist disease and cold. Particularly important before winter for cool-season grasses.',
    category: 'fertilizer'
  },
  'slow-release': {
    term: 'Slow-Release Fertilizer',
    shortDef: 'Fertilizer that releases nutrients gradually over weeks',
    longDef: 'Coated or organic fertilizer granules that break down slowly, feeding your lawn for 6-12 weeks. Lower risk of fertilizer burn, more consistent feeding, and fewer applications needed. Slightly more expensive but better for lawn health.',
    category: 'fertilizer'
  },
  'quick-release': {
    term: 'Quick-Release Fertilizer',
    shortDef: 'Fast-acting fertilizer that works within days',
    longDef: 'Water-soluble fertilizer that becomes available to grass immediately. Provides rapid greening but requires more frequent application. Higher risk of fertilizer burn if over-applied or applied in heat.',
    category: 'fertilizer'
  },
  'fertilizer-burn': {
    term: 'Fertilizer Burn',
    shortDef: 'Grass damage from too much fertilizer',
    longDef: 'Brown or yellow patches caused by excess nitrogen drawing moisture from grass roots. Prevent by following application rates, avoiding hot weather, and watering after application. Most lawns recover in 2-4 weeks with proper watering.',
    category: 'fertilizer'
  },

  // ============ HERBICIDE TERMS ============
  'pre-emergent': {
    term: 'Pre-Emergent Herbicide',
    shortDef: 'Weed preventer applied before weeds sprout',
    longDef: 'Creates a chemical barrier in the top layer of soil that prevents weed seeds from germinating. Must be applied BEFORE weeds appear; timing is critical. Does not kill existing weeds. Common active ingredients include prodiamine and dithiopyr.',
    category: 'herbicide'
  },
  'post-emergent': {
    term: 'Post-Emergent Herbicide',
    shortDef: 'Weed killer for existing weeds',
    longDef: 'Herbicide applied directly to actively growing weeds. Can be selective (kills only certain plants) or non-selective (kills all plants). Best applied when weeds are young and actively growing, typically in spring or fall.',
    category: 'herbicide'
  },
  'selective-herbicide': {
    term: 'Selective Herbicide',
    shortDef: 'Kills specific weeds without harming grass',
    longDef: 'Herbicide formulated to target broadleaf weeds (dandelions, clover) while leaving grass unharmed. Common examples include 2,4-D and dicamba. Always verify the product is safe for your specific grass type.',
    category: 'herbicide'
  },
  'broadleaf-weeds': {
    term: 'Broadleaf Weeds',
    shortDef: 'Weeds with wide, flat leaves (dandelions, clover)',
    longDef: 'Weeds that have wide leaves rather than grass-like blades. Includes dandelions, clover, plantain, and chickweed. Easier to control than grassy weeds because selective herbicides can target them without harming grass.',
    category: 'herbicide'
  },

  // ============ MOWING TERMS ============
  'one-third-rule': {
    term: 'One-Third Rule',
    shortDef: 'Never remove more than 1/3 of grass height at once',
    longDef: 'Fundamental mowing principle: cutting more than one-third of the grass blade stresses the plant, weakens roots, and invites disease. If grass is 4.5 inches tall, mow to no shorter than 3 inches. May require more frequent mowing during peak growth.',
    category: 'mowing'
  },
  'mowing-height': {
    term: 'Mowing Height',
    shortDef: 'The height setting on your mower',
    longDef: 'Each grass type has an optimal height range. Cool-season grasses (fescue, bluegrass) typically thrive at 3-4 inches. Warm-season grasses (bermuda, zoysia) prefer 1-2 inches. Taller heights promote deeper roots and better drought tolerance.',
    category: 'mowing'
  },
  'grass-cycling': {
    term: 'Grass Cycling',
    shortDef: 'Leaving clippings on the lawn as natural fertilizer',
    longDef: 'Also called "grasscycling." Clippings decompose quickly, returning nitrogen and nutrients to the soil. Can reduce fertilizer needs by up to 25%. Works best with sharp blades and regular mowing. Does not cause thatch buildup.',
    category: 'mowing'
  },
  'scalping': {
    term: 'Scalping',
    shortDef: 'Cutting grass too short, exposing brown stems',
    longDef: 'Removing too much grass at once, cutting into the brown stem area. Weakens grass, invites weeds, and looks unsightly. Often happens with dull blades, uneven ground, or waiting too long between mowings.',
    category: 'mowing'
  },

  // ============ WATERING TERMS ============
  'deep-watering': {
    term: 'Deep Watering',
    shortDef: 'Watering thoroughly but less frequently',
    longDef: 'Applying 1-1.5 inches of water in one session rather than light daily watering. Encourages roots to grow deeper seeking moisture, making grass more drought-tolerant. Water should penetrate 6-8 inches into soil.',
    category: 'watering'
  },
  'drought-stress': {
    term: 'Drought Stress',
    shortDef: 'Signs your lawn needs water',
    longDef: 'Early signs include grass blades folding or curling, footprints remaining visible after walking on lawn, and a blue-gray color. Prolonged stress leads to dormancy (brown color). Most grasses recover from dormancy with adequate water.',
    category: 'watering'
  },
  'evapotranspiration': {
    term: 'Evapotranspiration (ET)',
    shortDef: 'Water lost through evaporation and plant use',
    longDef: 'The combination of water evaporating from soil and transpiring through grass blades. Higher in hot, dry, windy conditions. Your watering should replace ET losses. Weather-based irrigation controllers use ET data to adjust schedules automatically.',
    category: 'watering'
  },

  // ============ AERATION TERMS ============
  'aeration': {
    term: 'Aeration',
    shortDef: 'Creating holes in soil to reduce compaction',
    longDef: 'Process of removing small plugs of soil or creating holes to allow air, water, and nutrients to reach grass roots. Relieves compaction, reduces thatch, and promotes healthier growth. Best done during active growing season for your grass type.',
    category: 'aeration'
  },
  'core-aeration': {
    term: 'Core Aeration',
    shortDef: 'Removing small soil plugs (most effective method)',
    longDef: 'Using a machine to pull out 2-3 inch plugs of soil, leaving holes and cores on the surface. The preferred method; cores break down and redistribute, improving soil structure. Holes allow deep root growth and better water penetration.',
    category: 'aeration'
  },
  'soil-compaction': {
    term: 'Soil Compaction',
    shortDef: 'Compressed soil that restricts root growth',
    longDef: 'Soil particles pressed tightly together, eliminating air pockets. Caused by foot traffic, heavy equipment, or clay soil. Compacted soil prevents water absorption, limits root growth, and creates thin, weak grass. Aeration is the primary remedy.',
    category: 'aeration'
  },
  'thatch': {
    term: 'Thatch',
    shortDef: 'Layer of dead organic matter between grass and soil',
    longDef: 'A layer of dead stems, roots, and debris at the soil surface. Thin thatch (under 0.5 inch) is beneficial, acting as mulch. Thick thatch (over 0.75 inch) prevents water and nutrients from reaching roots and harbors pests. Reduce with aeration and proper fertilizing.',
    category: 'aeration'
  },

  // ============ SEEDING TERMS ============
  'germination': {
    term: 'Germination',
    shortDef: 'When seeds sprout and begin growing',
    longDef: 'The process of a seed developing into a new plant. Requires adequate soil moisture, proper temperature (varies by grass type), and seed-to-soil contact. Cool-season grasses germinate in 7-21 days; warm-season grasses take 14-30 days.',
    category: 'seeding'
  },
  'overseeding': {
    term: 'Overseeding',
    shortDef: 'Spreading seed over existing lawn to thicken it',
    longDef: 'Adding new grass seed to an existing lawn to fill thin spots, introduce improved varieties, or increase density. Best done after aeration. Requires good seed-to-soil contact and consistent moisture for 2-3 weeks.',
    category: 'seeding'
  },
  'soil-temperature': {
    term: 'Soil Temperature',
    shortDef: 'Temperature of soil where seeds germinate',
    longDef: 'Critical for germination timing. Cool-season grasses germinate best when soil is 50-65°F. Warm-season grasses need 65-70°F or higher. Soil temp lags behind air temp by 2-4 weeks. Use a soil thermometer at 2-4 inch depth for accuracy.',
    category: 'seeding'
  },
  'dormant-seeding': {
    term: 'Dormant Seeding',
    shortDef: 'Planting seed in late fall/winter to germinate in spring',
    longDef: 'Spreading seed when soil is too cold for germination, allowing seeds to naturally stratify over winter and sprout in early spring. Works best for cool-season grasses in cold climates. Seeds benefit from freeze-thaw cycles that improve soil contact.',
    category: 'seeding'
  },

  // ============ GENERAL TERMS ============
  'cool-season-grass': {
    term: 'Cool-Season Grass',
    shortDef: 'Grasses that thrive in 60-75°F temperatures',
    longDef: 'Grass types including Kentucky Bluegrass, Tall Fescue, Fine Fescue, and Perennial Ryegrass. Grow actively in spring and fall, may go dormant in summer heat. Best for northern climates. Green year-round in moderate climates.',
    category: 'general'
  },
  'warm-season-grass': {
    term: 'Warm-Season Grass',
    shortDef: 'Grasses that thrive in 80-95°F temperatures',
    longDef: 'Grass types including Bermuda, Zoysia, St. Augustine, Centipede, Bahia, and Buffalo. Grow actively in summer, go dormant and turn brown in winter. Best for southern climates. Very drought and heat tolerant once established.',
    category: 'general'
  },
  'transition-zone': {
    term: 'Transition Zone',
    shortDef: 'Middle region where both grass types struggle',
    longDef: 'Geographic band across the middle United States where summers are too hot for cool-season grasses and winters are too cold for warm-season grasses. Lawn care is more challenging here. Common strategy: Tall Fescue or Zoysia, or accepting seasonal dormancy.',
    category: 'general'
  },
  'growing-season': {
    term: 'Growing Season',
    shortDef: 'When your grass is actively growing',
    longDef: 'The period when grass grows, uses nutrients, and can recover from stress. For cool-season grasses: spring and fall. For warm-season grasses: late spring through early fall. Most lawn care activities (fertilizing, seeding, aerating) should align with growing season.',
    category: 'general'
  },
  'dormancy': {
    term: 'Dormancy',
    shortDef: 'When grass goes brown to survive stress',
    longDef: 'A survival mechanism where grass stops growing and turns brown during extreme heat, cold, or drought. The crown and roots remain alive. Grass typically recovers when conditions improve. Avoid heavy traffic on dormant lawns.',
    category: 'general'
  },
  'soil-ph': {
    term: 'Soil pH',
    shortDef: 'Measure of soil acidity/alkalinity (ideal: 6.0-7.0)',
    longDef: 'Scale from 0-14 measuring how acidic or alkaline your soil is. Most grasses prefer slightly acidic soil (6.0-7.0). Low pH (acidic): add lime. High pH (alkaline): add sulfur. Extreme pH locks up nutrients, making fertilizer ineffective. Get a soil test for accurate reading.',
    category: 'general'
  },
}

/**
 * Helper function to get a term definition (case-insensitive)
 */
export function getTermDefinition(term: string): TermDefinition | undefined {
  // Try exact match first
  if (lawnCareGlossary[term]) {
    return lawnCareGlossary[term]
  }

  // Try case-insensitive match
  const lowerTerm = term.toLowerCase()
  const key = Object.keys(lawnCareGlossary).find(
    k => k.toLowerCase() === lowerTerm
  )

  return key ? lawnCareGlossary[key] : undefined
}

/**
 * Get all terms in a category
 */
export function getTermsByCategory(category: TermDefinition['category']): TermDefinition[] {
  return Object.values(lawnCareGlossary).filter(t => t.category === category)
}
